import { GetGuildProgressResponse, QuestConfig, QuestProgression, QuestReset, WebsocketEvent } from "@devour/client";
import GoVipOWQuestRewardCard from "@/components/goVip/GoVipOWQuestRewardCard";
import { useCallback, useMemo, useState } from "react";
import GuildQualifierCard from "@/pages/guilds/GuildQualifierCard";
import { useSelector } from "react-redux";
import { IStore } from "@/redux/defaultStore";
import FrameButton from "@/components/buttons/FrameButton";
import classNames from "classnames";
import { useQueryClient } from "@tanstack/react-query";
import useWebsocket from "@/hooks/useWebsocket";

interface Props {
    gameId: string;
    alwaysMobile?: boolean;
    questProgressions?: Array<QuestProgression>;
    guildProgress?: GetGuildProgressResponse;
    forceModalDesign?: boolean;
    questConfigs: Array<QuestConfig>;
    selectedResetFilter?: QuestReset;
}
const RESET_FILTERS = [
    {
        title: "Daily",
        resetType: QuestReset.DAILY,
    },
    {
        title: "Weekly",
        resetType: QuestReset.WEEKLY,
    },
    {
        title: "Monthly",
        resetType: QuestReset.MONTHLY,
    },
    {
        title: "All-Time",
        resetType: QuestReset.NONE,
    },
];
export default function OverwolfGoVipQuestRewards(props: Props) {
    const [isClaimingInProgress, setIsClaimingInProgress] = useState<boolean>(false);
    const isOnOverwolf = useSelector((store: IStore) => store.metaStore.isOnOverwolf);
    const queryClient = useQueryClient();
    const getInitialResetType = useCallback(() => {
        const countMap = props.questConfigs.reduce((acc, quest) => {
            acc[quest.questReset] = (acc[quest.questReset] || 0) + 1;
            return acc;
        }, {} as Record<string, number>);

        for (const filter of RESET_FILTERS) {
            const count = countMap[filter.resetType] || 0;
            if (count > 0) {
                return filter.resetType;
            }
        }
    }, [props.questConfigs]);

    useWebsocket(WebsocketEvent.QUESTPROGRESSED, () => {
        console.log("invalidating quest progression");
        queryClient.invalidateQueries({ queryKey: ["quest-progression-user"] });
    }, true);

    // Use parent-provided filter if available, otherwise manage locally
    const [localSelectedResetFilter, setLocalSelectedResetFilter] = useState(getInitialResetType);
    const selectedResetFilter = props.selectedResetFilter !== undefined ? props.selectedResetFilter : localSelectedResetFilter;
    const setSelectedResetFilter = props.selectedResetFilter !== undefined ? () => {} : setLocalSelectedResetFilter;
    const questStats = useMemo(() => {
        if (!props.questConfigs) {
            return [];
        }
        return props.questConfigs
            .filter((quest) => {
                return quest.questReset === selectedResetFilter;
            })
            .map((quest) => {
                const progression = props.questProgressions?.find((stat) => stat?.questConfigId === quest?.id);
                return {
                    quest,
                    progression,
                    isClaimed: progression?.claimedAt ?? false,
                    completed: progression?.isComplete ?? false,
                };
            });
    }, [props.questConfigs, props.questProgressions, selectedResetFilter]);
    const sortedQuests = useMemo(() => {
        return [...questStats].sort((a, b) => {
            const aIsClaimed = a.isClaimed || a.quest?.rewardConfigIds?.length === 0;
            const bIsClaimed = b.isClaimed || b.quest?.rewardConfigIds?.length === 0;

            // 1. Sort by sponsorships at top (highest priority)
            if (a.quest?.isSponsored && !b.quest?.isSponsored) {
                return -1;
            }
            if (!a.quest?.isSponsored && b.quest?.isSponsored) {
                return 1;
            }

            // 2. Within same sponsorship status, put !isClaimed && isCompleted at the top
            if (!aIsClaimed && a.completed && (bIsClaimed || !b.completed)) {
                return -1;
            }
            if (!bIsClaimed && b.completed && (aIsClaimed || !a.completed)) {
                return 1;
            }

            // 3. Sort the rest normally (by completion status)
            if (a.completed && !b.completed) {
                return 1;
            }
            if (!a.completed && b.completed) {
                return -1;
            }

            // 4. Put isClaimed && isCompleted at the bottom
            if (aIsClaimed && a.completed && (!bIsClaimed || !b.completed)) {
                return 1;
            }
            if (bIsClaimed && b.completed && (!aIsClaimed || !a.completed)) {
                return -1;
            }

            return 0;
        });
    }, [questStats]);

    return (
        <>
            {/* Only show filter buttons if not controlled by parent */}
            {props.selectedResetFilter === undefined &&
                <div className="overwolf-govip_rewards_quest_filters">
                    {RESET_FILTERS.map((resetFilter) =>
                        <FrameButton
                            size="narrow"
                            color="gray"
                            className={classNames("overwolf-govip_rewards_quest_filters_btn", {
                                "selected": resetFilter.resetType === selectedResetFilter,
                            })}
                            key={resetFilter.resetType}
                            onClick={() => setSelectedResetFilter(resetFilter.resetType)}
                        >
                            {resetFilter.title}
                        </FrameButton>)}
                </div>
            }
            <div className="overwolf-govip_rewards_quest_list">
                {props.guildProgress && !props.guildProgress?.hasUserMintedBadge &&
                    <GuildQualifierCard
                        quests={props.guildProgress.quests}
                        guild={props.guildProgress.guild}
                        badgeImageUrl={props.guildProgress.badgeImageUrl}
                        isOnModal={!isOnOverwolf}
                    />
                }
                {sortedQuests.map(({ quest }) => {
                    return (
                        <GoVipOWQuestRewardCard
                            forceModalDesign={props.forceModalDesign}
                            className="overlay-card"
                            quest={quest}
                            alwaysMobile={props.alwaysMobile}
                            gameId={props.gameId}
                            isClaimingInProgress={isClaimingInProgress}
                            setIsClaimingInProgress={setIsClaimingInProgress}
                            key={quest.id}
                        />
                    );
                })}
                {props.guildProgress && props.guildProgress?.hasUserMintedBadge &&
                    <GuildQualifierCard
                        quests={props.guildProgress.quests}
                        guild={props.guildProgress.guild}
                        badgeImageUrl={props.guildProgress.badgeImageUrl}
                        isOnModal={!isOnOverwolf}
                    />
                }
            </div>
        </>
    );
}
